import { useCallback, useEffect, useMemo, useState } from "react"
import { useRouter } from "next/router"

import { CACHE_PROFILES } from "@/config/query-cache"
import {
  useTransportMutation,
  useTransportQuery,
  useTransportQueryUtils,
} from "@/transport"
import { useOrganization } from "@clerk/nextjs"
import { toast } from "@coval/ui"
import { useQueryClient } from "@tanstack/react-query"
import { nanoid } from "nanoid"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { logger } from "@/lib/logger"
import { useQueryParams } from "@/hooks/useQueryParams"
import { toISODateString } from "@/utils/date/date-range-helpers"
import { downloadBlob } from "@/utils/download"

import type { DashboardMeta } from "../types/core"
import {
  mapTransportDashboardMeta,
  mapTransportDashboardWidget,
  serializeCreateDashboardRequest,
  serializeDashboardMetadataForTransport,
  serializeDeleteDashboardRequest,
  serializeGetDashboardLayoutRequest,
  serializeUpdateDashboardRequest,
  type DashboardMetadataResource,
} from "../utils/transportDashboard"
import { isTransportDashboardQueryLoading } from "../utils/transportDashboardQueries"
import { parseImportedDashboardWidgets } from "../utils/widgetConfigBoundaries"
import { useDashboardWidgetActions } from "./useDashboardWidgetActions"
import {
  createDashboardDeletedSaveError,
  toDashboardTransportError,
  useDashboardWidgetSaveQueue,
} from "./useDashboardWidgetSaveQueue"

const log = logger("DashboardWidgets")

export const useDashboardWidgetsTransport = (
  initialDashboardId?: string,
  options?: { fetchWidgets?: boolean },
) => {
  const fetchWidgets = options?.fetchWidgets ?? true
  const queryClient = useQueryClient()
  const router = useRouter()
  const { organization } = useOrganization()
  const transportQueryUtils = useTransportQueryUtils()
  const queryParams = useQueryParams()
  const { trackEvent } = useAnalytics()
  const [currentDashboardId, setCurrentDashboardId] = useState<
    string | undefined
  >(initialDashboardId)

  const dashboardScope = useMemo(() => {
    const routeSlug =
      typeof router.query.slug === "string" ? router.query.slug : null
    const organizationId = organization?.id ?? null
    const organizationSlug = organization?.slug ?? null

    if (!organizationId && !organizationSlug && !routeSlug) {
      return null
    }

    return [
      organizationId ?? "unknown",
      organizationSlug ?? routeSlug ?? "unknown",
    ].join(":")
  }, [organization?.id, organization?.slug, router.query.slug])

  const transportLayoutRequest = useMemo(
    () =>
      currentDashboardId
        ? serializeGetDashboardLayoutRequest(currentDashboardId)
        : ({} as const),
    [currentDashboardId],
  )

  // Fetch all dashboards (metadata). Always enabled because the sidebar
  // DashboardNavPopover needs the list on every page.
  const transportDashboardsQuery = useTransportQuery({
    operation: "listDashboardMetadata",
    options: {
      ...CACHE_PROFILES.lookup,
    },
  })

  const dashboards = useMemo(
    () =>
      transportDashboardsQuery.data?.dashboards.map((dashboard) =>
        mapTransportDashboardMeta(dashboard, organization?.id),
      ) ?? [],
    [organization?.id, transportDashboardsQuery.data?.dashboards],
  )
  const dashboardsLoading = isTransportDashboardQueryLoading(
    transportDashboardsQuery,
  )
  const dashboardsError = transportDashboardsQuery.error

  // Sync with initialDashboardId when it becomes available (e.g. after router.isReady)
  useEffect(() => {
    if (initialDashboardId) {
      setCurrentDashboardId(initialDashboardId)
    }
  }, [initialDashboardId])

  // Set default dashboardId if not set (only when widget fetching is enabled).
  // On org switches, clear stale index-route selections from the previous org.
  useEffect(() => {
    if (!fetchWidgets || initialDashboardId || dashboards.length === 0) {
      return
    }

    const selectedDashboardExists = dashboards.some(
      (dashboard) => dashboard.id === currentDashboardId,
    )

    if (!currentDashboardId || !selectedDashboardExists) {
      const defaultDashboard =
        dashboards.find((dashboard) => dashboard.is_default) || dashboards[0]
      setCurrentDashboardId(defaultDashboard.id)
    }
  }, [currentDashboardId, dashboards, fetchWidgets, initialDashboardId])

  // Fetch widgets for the selected dashboard (skipped on non-dashboard pages)
  const layoutQueryEnabled = fetchWidgets && !!currentDashboardId
  const transportLayoutQuery = useTransportQuery({
    operation: "getDashboardLayout",
    request: transportLayoutRequest,
    options: {
      enabled: layoutQueryEnabled,
    },
  })
  const baseWidgets = useMemo(
    () =>
      transportLayoutQuery.data?.widgets.map((widget) =>
        mapTransportDashboardWidget(widget),
      ) ?? [],
    [transportLayoutQuery.data?.widgets],
  )
  const widgetsLoading = isTransportDashboardQueryLoading(
    transportLayoutQuery,
    layoutQueryEnabled,
  )
  const widgetsError = transportLayoutQuery.error

  const {
    cancelDashboardSaves,
    clearTransportWidgetOverride,
    currentWidgetsRef,
    enqueueDashboardSave,
    isSavingDashboard,
    safeEnqueueDashboardSave,
    widgets,
  } = useDashboardWidgetSaveQueue({
    baseWidgets,
    currentDashboardId,
    dashboardScope,
    dashboards,
    layoutData: transportLayoutQuery.data,
    setCurrentDashboardId,
  })

  const transportCreateDashboardMutation =
    useTransportMutation("createDashboard")
  const transportUpdateDashboardMutation =
    useTransportMutation("updateDashboard")
  const transportDeleteDashboardMutation =
    useTransportMutation("deleteDashboard")

  const setTransportDashboardListCache = useCallback(
    (updater: (dashboards: DashboardMeta[]) => DashboardMeta[]) => {
      queryClient.setQueryData(
        transportQueryUtils.getKey("listDashboardMetadata"),
        (existingData: unknown) => {
          const typedExistingData = existingData as
            | {
                dashboards: DashboardMetadataResource[]
              }
            | undefined

          if (!typedExistingData) {
            return existingData
          }

          const mappedDashboards = typedExistingData.dashboards.map(
            (dashboard) =>
              mapTransportDashboardMeta(dashboard, organization?.id),
          )
          const updatedDashboards = updater(mappedDashboards)

          return {
            dashboards: updatedDashboards.map(
              serializeDashboardMetadataForTransport,
            ),
          }
        },
      )
    },
    [organization?.id, queryClient, transportQueryUtils],
  )

  const invalidateTransportDashboardQueries = useCallback(
    (dashboardId?: string) => {
      void queryClient.invalidateQueries({
        queryKey: transportQueryUtils.getKey("listDashboardMetadata"),
      })

      if (!dashboardId) {
        return
      }

      void queryClient.invalidateQueries({
        queryKey: transportQueryUtils.getKey(
          "getDashboardLayout",
          serializeGetDashboardLayoutRequest(dashboardId),
        ),
      })
    },
    [queryClient, transportQueryUtils],
  )

  const {
    handleAddWidget,
    handleClearDashboard,
    handleCloneWidget,
    handleRemoveWidget,
    handleUpdateWidget,
    handleWidgetMove,
    handleWidgetWidthChange,
  } = useDashboardWidgetActions({
    currentDashboardId,
    currentWidgetsRef,
    enqueueDashboardSave,
    safeEnqueueDashboardSave,
    trackEvent,
  })

  const handleCreateDashboard = useCallback(
    async (displayName: string) => {
      const response = await transportCreateDashboardMutation.mutateAsync(
        serializeCreateDashboardRequest(displayName),
      )
      const createdDashboard = mapTransportDashboardMeta(
        response.dashboard,
        organization?.id,
      )
      setTransportDashboardListCache((existingDashboards) => [
        ...existingDashboards,
        createdDashboard,
      ])
      setCurrentDashboardId(createdDashboard.id)

      trackEvent(TRACKING_EVENTS.DASHBOARD.CREATED, {
        dashboard_id: createdDashboard.id,
        dashboard_name: displayName,
      })

      invalidateTransportDashboardQueries(createdDashboard.id)
      return createdDashboard
    },
    [
      invalidateTransportDashboardQueries,
      organization?.id,
      trackEvent,
      setTransportDashboardListCache,
      transportCreateDashboardMutation,
    ],
  )

  const handleRenameDashboard = useCallback(
    async (dashboardId: string, newName: string) => {
      const response = await transportUpdateDashboardMutation.mutateAsync(
        serializeUpdateDashboardRequest({
          dashboardId,
          displayName: newName,
        }),
      )
      const updatedDashboard = mapTransportDashboardMeta(
        response.dashboard,
        organization?.id,
      )
      setTransportDashboardListCache((existingDashboards) =>
        existingDashboards.map((dashboard) =>
          dashboard.id === dashboardId ? updatedDashboard : dashboard,
        ),
      )
      invalidateTransportDashboardQueries(dashboardId)
    },
    [
      invalidateTransportDashboardQueries,
      organization?.id,
      setTransportDashboardListCache,
      transportUpdateDashboardMutation,
    ],
  )

  const handleDeleteDashboard = useCallback(
    async (dashboardId: string) => {
      try {
        const isDeletingCurrentDashboard = dashboardId === currentDashboardId

        await transportDeleteDashboardMutation.mutateAsync(
          serializeDeleteDashboardRequest(dashboardId),
        )
        cancelDashboardSaves(dashboardId, createDashboardDeletedSaveError())
        if (isDeletingCurrentDashboard) {
          setCurrentDashboardId(undefined)
        }
        setTransportDashboardListCache((existingDashboards) =>
          existingDashboards.filter(
            (dashboard) => dashboard.id !== dashboardId,
          ),
        )
        clearTransportWidgetOverride(dashboardId)
        invalidateTransportDashboardQueries(
          isDeletingCurrentDashboard ? undefined : dashboardId,
        )

        toast.success({
          title: "Dashboard deleted",
          description: "The dashboard has been deleted.",
        })

        if (isDeletingCurrentDashboard && organization?.slug) {
          void router.push(queryParams.getHref("/home", organization.slug))
        }
      } catch (error) {
        const normalizedError = toDashboardTransportError(
          error,
          "Failed to delete dashboard",
        )
        toast.danger({
          title: "Failed to delete dashboard",
          description: "The dashboard couldn't be deleted. Please try again.",
        })
        log.error("Failed to delete dashboard", normalizedError)
      }
    },
    [
      cancelDashboardSaves,
      currentDashboardId,
      invalidateTransportDashboardQueries,
      organization?.slug,
      queryParams,
      router,
      clearTransportWidgetOverride,
      setTransportDashboardListCache,
      transportDeleteDashboardMutation,
    ],
  )

  const handleToggleFavorite = useCallback(
    async (dashboardId: string) => {
      const dashboard = dashboards.find((entry) => entry.id === dashboardId)
      if (!dashboard) {
        return
      }

      const response = await transportUpdateDashboardMutation.mutateAsync(
        serializeUpdateDashboardRequest({
          dashboardId,
          isFavorite: !dashboard.is_favorite,
        }),
      )
      const updatedDashboard = mapTransportDashboardMeta(
        response.dashboard,
        organization?.id,
      )
      setTransportDashboardListCache((existingDashboards) =>
        existingDashboards.map((existingDashboard) =>
          existingDashboard.id === dashboardId
            ? updatedDashboard
            : existingDashboard,
        ),
      )
      invalidateTransportDashboardQueries(dashboardId)
    },
    [
      dashboards,
      invalidateTransportDashboardQueries,
      organization?.id,
      setTransportDashboardListCache,
      transportUpdateDashboardMutation,
    ],
  )

  // Export/Import
  const handleExportDashboard = useCallback(() => {
    try {
      const dataStr = JSON.stringify(widgets, null, 2)
      const blob = new Blob([dataStr], { type: "application/json" })
      downloadBlob(blob, `dashboard-widgets-${toISODateString()}.json`)
    } catch (error) {
      log.error("Failed to export dashboard", error)
    }
  }, [widgets])

  const handleImportDashboard = useCallback(
    (file: File) => {
      return new Promise<void>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (event) => {
          try {
            const result = event.target?.result
            if (typeof result !== "string") {
              reject(new Error("Invalid file format"))
              return
            }

            const importedWidgets = parseImportedDashboardWidgets(
              JSON.parse(result) as unknown,
            )

            const dashboardId = currentDashboardId

            if (!importedWidgets || !dashboardId) {
              reject(new Error("Invalid file format"))
              return
            }

            const processedWidgets = importedWidgets.map((widget) => ({
              ...widget,
              id: nanoid(),
            }))

            void enqueueDashboardSave(dashboardId, processedWidgets)
              .then(() => resolve())
              .catch((error) =>
                reject(
                  toDashboardTransportError(
                    error,
                    "Failed to import dashboard",
                  ),
                ),
              )
          } catch (error) {
            reject(
              toDashboardTransportError(error, "Failed to import dashboard"),
            )
          }
        }
        reader.onerror = () => reject(new Error("Failed to read file"))
        reader.readAsText(file)
      })
    },
    [currentDashboardId, enqueueDashboardSave],
  )

  return {
    dashboards,
    currentDashboardId,
    setCurrentDashboardId,
    widgets,
    isSavingDashboard,
    isLoading: dashboardsLoading || widgetsLoading,
    error: dashboardsError || widgetsError,
    /** Error from the dashboard list fetch (for nav popover). */
    dashboardsListError: dashboardsError,
    handleAddWidget,
    handleRemoveWidget,
    handleCloneWidget,
    handleUpdateWidget,
    handleClearDashboard,
    handleExportDashboard,
    handleImportDashboard,
    handleCreateDashboard,
    handleRenameDashboard,
    handleDeleteDashboard,
    handleToggleFavorite,
    handleWidgetMove,
    handleWidgetWidthChange,
  }
}
